import {
	DeleteOutlined,
	LockOutlined,
	PlusOutlined,
	UserOutlined,
} from "@ant-design/icons";
import {
	Alert,
	Button,
	Card,
	Col,
	Form,
	Input,
	InputNumber,
	Modal,
	Row,
	Select,
	Space,
	Table,
	Tag,
	Tooltip,
	Typography,
} from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";
import db from "../../db/database";
import {
	addInvoiceLine,
	deleteInvoiceLine,
	updateInvoiceLine,
} from "../../services/invoiceService";
import { getProductByBarcode } from "../../services/productService";
import { getSalesPriceWithPriority, getVatByProductAndClient } from "../../services/salesPriceService";
import { formatNumberByCountry } from "../../utils/formatNumbers";
import { calculateLineAmounts } from "../../utils/invoiceTotals";
import { getInvoiceFormConfig } from "./configs/invoiceForm.config";
import { getInvoiceLinesConfig } from "./configs/invoiceLines.config";

const sanitizeLines = (lines) => {
	return lines.map((line) => ({
		...line,
		Line_Discount_: Number(parseFloat(line.Line_Discount_ || 0).toFixed(2)),
		Client_Discount_: Number(parseFloat(line.Client_Discount_ || 0).toFixed(2)),
		Coupon_Discount_: Number(parseFloat(line.Coupon_Discount_ || 0).toFixed(2)),
		Unit_Price: Number(parseFloat(line.Unit_Price || 0).toFixed(2)),
		PorIva: Number(parseFloat(line.PorIva || 0).toFixed(2)),
	}));
};

const InvoiceLines = ({
	invoice,
	lines,
	setLines,
	products,
	isSynchronized,
	isInvoiceEditing,
	selectedClientDiscount,
	loading,
	selectedClient,
	selectedColegio,
	selectedCouponDetail,
	onPaymentLinesUpdate, // Nueva prop para actualizar líneas de pago automáticamente
	documentType, // Nueva prop para el tipo de documento
	totals,
	country
}) => {
	const [scanCode, setScanCode] = useState("");
	const [selectedProduct, setSelectedProduct] = useState(null);
	const scanInputRef = useRef(null);
	const [editingLine, setEditingLine] = useState(null);
	const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
	const [tempQuantity, setTempQuantity] = useState(null);
	const [loadingProduct, setLoadingProduct] = useState(false);
	const [zeroPriceModalVisible, setZeroPriceModalVisible] = useState(false);
	const [zeroPriceProductInfo, setZeroPriceProductInfo] = useState(null);

	// Estados para autorización de administrador para descuentos
	const [adminAuthModal, setAdminAuthModal] = useState(false);
	const [adminForm] = Form.useForm();
	const [adminAuthLoading, setAdminAuthLoading] = useState(false);
	const [pendingDiscountData, setPendingDiscountData] = useState(null);
	const [editingDiscount, setEditingDiscount] = useState(null);
	const [tempDiscount, setTempDiscount] = useState(null);

	// Verificar si es un abono (Credit Memo)
	const isCreditMemo = documentType === "Credit Memo";

	const [globalDiscountModal, setGlobalDiscountModal] = useState(false);
	const [globalDiscountValue, setGlobalDiscountValue] = useState(0);
	const [priceIncreaseModalVisible, setPriceIncreaseModalVisible] = useState(false);
	const [newPriceValue, setNewPriceValue] = useState(null);
	const [selectedPriceIncreaseLine, setSelectedPriceIncreaseLine] = useState(null);

	// Config por pais
	const config = getInvoiceFormConfig(country.code);
	const { requiredFields } = config;
	const invoiceLinesConfig = getInvoiceLinesConfig(country.code);
	const {
		canApplyPriceIncrease = false,
		requireAuthorizationForPriceIncrease = () => true,
	} = invoiceLinesConfig;

	// Función para validar credenciales de administrador
	const validateAdminCredentials = async (username, password) => {
		try {
			// Buscar el usuario en la base de datos por su No (igual que en loginUser)
			const user = await db.users
				.where("No")
				.equals(username)
				.and((user) => !user.deleted) // Asegurar que no está eliminado
				.first();

			if (!user) {
				return false; // Usuario no encontrado
			}

			// Verificar contraseña
			if (user.password !== password) {
				return false; // Contraseña incorrecta
			}

			// Verificar si es administrador
			if (!user.Admin) {
				return false; // No es administrador
			}

			return true; // Usuario válido y es administrador
		} catch (error) {
			console.error("Error al validar credenciales de administrador:", error);
			return false;
		}
	};

	// Función para manejar la autorización de administrador para descuentos
	const handleAdminAuthForDiscount = async (adminValues) => {
		try {
			setAdminAuthLoading(true);

			const { username, password } = adminValues;

			const isValid = await validateAdminCredentials(username, password);
			if (!isValid) {
				window.messageApi.error("Credenciales de administrador incorrectas");
				return;
			}

			else {
				window.messageApi.success("Autorización de administrador concedida");
				setAdminAuthModal(false);
				adminForm.resetFields();

				if (pendingDiscountData?.type === "deleteAllLines") {
					await pendingDiscountData.applyDeleteAll();
				} else if (pendingDiscountData?.type === "priceIncreaseLine") {
					await pendingDiscountData.applyToLine();
				} else if (pendingDiscountData?.type === "priceIncreaseGlobal") {
					await pendingDiscountData.applyToAll();
				} else if (pendingDiscountData?.isGlobal) {
					await pendingDiscountData.applyToAll();
				} else if (pendingDiscountData) {
					await applyDiscountAfterAuth(pendingDiscountData);
				}
			}
		} catch (error) {
			console.error("Error en autorización de administrador:", error);
			window.messageApi.error("Error durante la autorización");
		} finally {
			setAdminAuthLoading(false);
		}
	};

	// Función para aplicar el descuento después de la autorización
	const applyDiscountAfterAuth = async ({ line, newDiscount }) => {
		try {
			const updatedLines = lines.map((l) => {
				if (l.key === line.key) {
					// Mantener los descuentos originales de cliente y cupón
					const clientDiscount = l.Client_Discount_ || 0;
					const couponDiscount = l.Coupon_Discount_ || 0;

					return {
						...l,
						Line_Discount_: newDiscount,
						// Mantener los descuentos separados para mostrar en la UI
						Client_Discount_: clientDiscount,
						Coupon_Discount_: couponDiscount,
					};
				}
				return l;
			});

			setLines(sanitizeLines(updatedLines));

			if (isInvoiceEditing && line.id) {
				await updateInvoiceLine(line.id, {
					...line,
					Line_Discount_: newDiscount,
				});
			}

			setEditingDiscount(null);
			setTempDiscount(null);
			setPendingDiscountData(null);

			window.messageApi.success("Descuento aplicado correctamente");
		} catch (error) {
			console.error("Error al aplicar descuento:", error);
			window.messageApi.error("No se pudo aplicar el descuento");
		}
	};

	// Función para cancelar la autorización de administrador
	const handleCancelAdminAuth = () => {
		setAdminAuthModal(false);
		adminForm.resetFields();
		setPendingDiscountData(null);
		setEditingDiscount(null);
		setTempDiscount(null);
	};

	const handleOpenPriceIncreaseModal = (line) => {
		if (isSynchronized) {
			window.messageApi.error(
				"No se pueden editar líneas de una factura sincronizada con Business Central"
			);
			return;
		}
		setSelectedPriceIncreaseLine(line);
		setNewPriceValue(null);
		setPriceIncreaseModalVisible(true);
	};

	const handleConfirmPriceIncrease = async () => {
		if (!selectedPriceIncreaseLine) {
			window.messageApi.warning("Seleccione una línea para aplicar la subida");
			return;
		}
		const currentPrice = Number(selectedPriceIncreaseLine.Unit_Price || 0);
		const proposedPrice = Number(newPriceValue);
		if (!Number.isFinite(proposedPrice)) {
			window.messageApi.warning("Ingrese un precio válido");
			return;
		}
		if (proposedPrice <= currentPrice) {
			window.messageApi.warning(
				"El nuevo precio debe ser mayor al precio actual"
			);
			return;
		}

		const applyToLine = async () => {
			try {
				const updatedLines = lines.map((line) => {
					if (line.key !== selectedPriceIncreaseLine.key) {
						return line;
					}
					return { ...line, Unit_Price: Number(proposedPrice.toFixed(2)) };
				});

				setLines(sanitizeLines(updatedLines));

				if (isInvoiceEditing && selectedPriceIncreaseLine.id) {
					const updatedLine = updatedLines.find((line) => line.key === selectedPriceIncreaseLine.key);
					if (updatedLine) {
						await updateInvoiceLine(selectedPriceIncreaseLine.id, updatedLine);
					}
				}

				window.messageApi.success("Precio actualizado correctamente");
				setPriceIncreaseModalVisible(false);
				setNewPriceValue(null);
				setSelectedPriceIncreaseLine(null);
				setPendingDiscountData(null);
			} catch (error) {
				console.error("Error al aplicar subida de precio:", error);
				window.messageApi.error("No se pudo aplicar la subida de precio");
			}
		};
		const requiresAuthorization = requireAuthorizationForPriceIncrease(
			proposedPrice,
			currentPrice
		);
		if (!requiresAuthorization) {
			await applyToLine();
			return;
		}
		setPendingDiscountData({ type: "priceIncreaseLine", applyToLine });
		setPriceIncreaseModalVisible(false);
		setAdminAuthModal(true);
	};

	// Función para manejar el cambio de descuento
	const handleDiscountChange = (line, newDiscount) => {
		if (isSynchronized) {
			window.messageApi.error(
				"No se pueden editar líneas de una factura sincronizada con Business Central"
			);
			return;
		}

		// Validar que el descuento esté en el rango permitido
		if (newDiscount < 0 || newDiscount > 100) {
			window.messageApi.warning("El descuento debe estar entre 0% y 100%");
			return;
		}

		// Solo actualizar el valor temporal
		setTempDiscount(newDiscount);
	};

	// Función para confirmar el cambio de descuento
	const handleDiscountConfirm = async (line) => {
		if (isSynchronized) {
			window.messageApi.error(
				"No se pueden editar líneas de una factura sincronizada con Business Central"
			);
			return;
		}

		// Si no hay un valor temporal o es el mismo que tenemos, no hacemos nada
		if (tempDiscount === null || tempDiscount === line.Line_Discount_) {
			setEditingDiscount(null);
			setTempDiscount(null);
			return;
		}

		// Requerir autorización de administrador para editar descuentos segun pais
		const { requireAuthorizationForDiscount, maxLineDiscount } = invoiceLinesConfig;

		if (tempDiscount > maxLineDiscount) {
			setEditingDiscount(null);
			setTempDiscount(null);
			window.messageApi.warning(`El descuento debe estar entre 0% y ${maxLineDiscount}%`, { modal: true });
			return;
		}

		const isAuthorizationRequired = requireAuthorizationForDiscount(tempDiscount);

		if (isAuthorizationRequired) {
			setPendingDiscountData({ line, newDiscount: tempDiscount });
			setAdminAuthModal(true);
		}
		else {
			await applyDiscountAfterAuth({ line, newDiscount: tempDiscount });

		}
	};

	// Detectar cambios en el tamaño de la pantalla
	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < 768);
		};

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);
	

	// Función para calcular el descuento de cupón para un producto específico
	const calculateCouponDiscount = useCallback(
		(productNo) => {
			if (
				!selectedCouponDetail ||
				!selectedCouponDetail.originalCoupon?.Line_Discounts
			) {
				return 0;
			}

			const lineDiscounts = selectedCouponDetail.originalCoupon.Line_Discounts;

			const applicableDiscount = lineDiscounts.find(
				(discount) => discount.Code === productNo
			);

			let descuento = 0;

			//Prioridad descuentos:
			// 1) Line_Discount_Percentage
			// 2) Descuento detalle del cupón
			if (applicableDiscount && applicableDiscount.Line_Discount_Percentage > 0) {
				descuento = applicableDiscount.Line_Discount_Percentage;

			} else if (selectedCouponDetail.detailDiscountPercentage > 0) {
				descuento = selectedCouponDetail.detailDiscountPercentage;
			}

			return descuento;
		},
		[selectedCouponDetail]
	);

	// Función para manejar la selección de un producto desde el selector
	const handleProductSelect = async (value) => {
		try {
			if (!value) return;

			const product = products.find((p) => p.No === value);
			if (product) {
				// Limpiar la selección inmediatamente para cerrar el desplegable
				setSelectedProduct(null);

				// Activar spinner y permitir que la UI se actualice
				setLoadingProduct(true);

				// Usar setTimeout para permitir que la UI se actualice antes del procesamiento
				setTimeout(async () => {
					try {
						// Procesar el producto seleccionado como si se hubiera escaneado
						await processProduct(product);
					} catch (processingError) {
						console.error("Error al procesar producto:", processingError);
						window.messageApi.error("Error al procesar el producto");
					} finally {
						setLoadingProduct(false); // Desactivar spinner
					}
				}, 50); // 50ms es suficiente para que la UI se actualice
			}
		} catch (error) {
			console.error("Error al seleccionar producto:", error);
			window.messageApi.error("Error al seleccionar el producto");
			setLoadingProduct(false); // Asegurar que se desactive el spinner en caso de error
		}
	};

	// Función para procesar un producto (ya sea escaneado o seleccionado)
	const processProduct = async (product) => {
		try {
			// Comprobar si el producto ya está en las líneas
			const existingLineIndex = lines.findIndex(
				(line) => line.No === product.No && !line._isDeleted
			);

			if (existingLineIndex >= 0) {
				// Si el producto ya existe, solo incrementar la cantidad
				const updatedLines = [...lines];
				updatedLines[existingLineIndex] = {
					...updatedLines[existingLineIndex],
					Quantity: (updatedLines[existingLineIndex].Quantity || 0) + 1,
				};

				if (isInvoiceEditing && updatedLines[existingLineIndex].id) {
					// Si estamos editando y la línea tiene ID, actualizar en BD
					await updateInvoiceLine(
						updatedLines[existingLineIndex].id,
						updatedLines[existingLineIndex]
					);
				}

				setLines(sanitizeLines(updatedLines));
				window.messageApi.success(
					`Cantidad de ${product.description} aumentada`
				);
			} else {
				// Obtener precio con prioridades
				let unitPrice = product.Unit_Price || 0;
				let salesPrice = null;

				try {
					// Validaciones antes de procesar precio
					const clientRequiredMissing = requiredFields.cliente && !selectedClient;
					const colegioRequiredMissing = requiredFields.colegio && !selectedColegio;

					const missingFields = [
						clientRequiredMissing ? "cliente" : null,
						colegioRequiredMissing ? "colegio" : null
					].filter(Boolean);

					if (clientRequiredMissing || colegioRequiredMissing) {
						window.messageApi.error(
							`No se puede agregar el producto: Faltan los siguientes datos obligatorios → ${missingFields.join(", ")}.`
						);

						return;
					}

					// Buscar precio con prioridades
					const clientNo = selectedClient?.No;
					const customerPriceGroup = selectedClient?.Customer_Price_Group;
					const colegioNo = selectedColegio;

					salesPrice = await getSalesPriceWithPriority(
						product,
						clientNo,
						customerPriceGroup,
						colegioNo,
						country
					);

					if (
						salesPrice &&
						salesPrice.Unit_Price !== null &&
						salesPrice.Unit_Price !== undefined &&
						salesPrice.Unit_Price !== 0
					) {
						unitPrice = salesPrice.Unit_Price;
					}
					else {
						// Verificar si el precio es 0 - mostrar aviso y no agregar la linea
						setZeroPriceProductInfo({
							name: product.description,
							no: product.No,
							clientName: selectedClient?.name || "Cliente no seleccionado",
							clientNo: selectedClient?.No || selectedClient?.id || "Sin número",
						});
						setZeroPriceModalVisible(true);
						return;
					}
				} catch (priceError) {
					console.warn(
						"Error al obtener precio especial, usando precio estándar:",
						priceError
					);
					// Continuar con el precio estándar del producto
				}

				// Calcular descuentos
				const clientDiscount = selectedClientDiscount || 0;
				const couponDiscount = calculateCouponDiscount(product.No);
				const totalDiscount = clientDiscount + couponDiscount;

				// Obtener impuesto
				const VAT = await getVatByProductAndClient(product, selectedClient);

				// Crear nueva línea
				const newLine = {
					key: Date.now().toString(),
					No: product.No,
					Description: product.description,
					Unit_Price: unitPrice,
					Quantity: 1,
					Type: "Item",
					Line_Discount_: totalDiscount,
					Client_Discount_: clientDiscount,
					Coupon_Discount_: couponDiscount,
					PorIva: VAT?.VAT_Percent || 0, // IVA específico del producto
					Price_Includes_VAT: product.Price_Includes_VAT
				};

				if (isInvoiceEditing) {
					// Si la factura ya existe, guardar en BD
					const savedLine = await addInvoiceLine({
						...newLine,
						invoiceId: invoice.id,
						Document_Type: invoice.Document_Type || "Invoice",
					});

					setLines([...lines, { ...savedLine, key: savedLine.id.toString() }]);
				} else {
					// Si es factura nueva, solo agregar localmente
					setLines([...lines, { ...newLine, _isLocalLine: true }]);
				}

				window.messageApi.success(`${product.description} agregado`);
			}

			// Enfocar el input para el siguiente escaneo
			setTimeout(() => {
				scanInputRef.current?.focus();
			}, 100);
		} catch (error) {
			console.error("Error al procesar producto:", error);
			window.messageApi.error("Error al procesar el producto");
			setTimeout(() => {
				scanInputRef.current?.focus();
			}, 100);
		}
	};

	// Función principal para escanear/agregar productos
	const handleScan = async () => {
		if (!scanCode.trim()) return;

		try {
			setLoadingProduct(true); // Activar spinner

			// Buscar producto por código de barras o No
			let product = await getProductByBarcode(scanCode);

			if (!product) {
				product = products.find((p) => p.No === scanCode);
			}

			if (!product) {
				window.messageApi.error(
					`No se encontró el producto con código ${scanCode}`
				);
				setScanCode("");
				setTimeout(() => {
					scanInputRef.current?.focus();
				}, 100);
				return;
			}

			// Procesar el producto encontrado
			await processProduct(product);

			// Limpiar campo de escaneo
			setScanCode("");
		} catch (error) {
			console.error("Error al procesar producto:", error);
			window.messageApi.error("Error al procesar el producto");
			setScanCode("");
			setTimeout(() => {
				scanInputRef.current?.focus();
			}, 100);
		} finally {
			setLoadingProduct(false); // Desactivar spinner
		}
	};

	// Eliminar una línea
	const handleDeleteLine = async (line) => {
		if (isSynchronized) {
			window.messageApi.error(
				"No se pueden eliminar líneas de una factura sincronizada con Business Central"
			);
			return;
		}

		// Para líneas sin ID en BD o líneas locales, eliminar directamente
		if (!line.id || line._isLocalLine) {
			try {
				setLines(lines.filter((l) => l.key !== line.key));
				window.messageApi.success("Línea eliminada correctamente");
			} catch (error) {
				console.error("Error al eliminar línea local:", error);
				window.messageApi.error("Error al eliminar la línea");
			}
			return;
		}

		// Para líneas con ID en BD, eliminar directamente sin confirmación
		try {
			// Si estamos editando una factura existente, eliminar de BD
			if (isInvoiceEditing && line.id) {
				await deleteInvoiceLine(line.id);
			}

			// Eliminar de la lista local
			setLines(lines.filter((l) => l.key !== line.key));
			window.messageApi.success("Línea eliminada correctamente");
		} catch (error) {
			console.error("Error al eliminar línea:", error);

			// Proporcionar mensaje de error más específico
			if (error.response) {
				const status = error.response.status;
				const message = error.response.data?.message || error.message;

				if (status === 404) {
					window.messageApi.error("La línea ya no existe en la base de datos");
					// Eliminar de la lista local de todas formas
					setLines(lines.filter((l) => l.key !== line.key));
				} else if (status === 403) {
					window.messageApi.error(
						"No tienes permisos para eliminar esta línea"
					);
				} else {
					window.messageApi.error(`Error del servidor: ${message}`);
				}
			} else if (error.request) {
				window.messageApi.error(
					"Error de conexión. Verifica tu conexión a internet"
				);
			} else {
				window.messageApi.error(`Error inesperado: ${error.message}`);
			}
		}
	};

	const handleDeleteAllLines = async () => {
		if (isSynchronized) {
			window.messageApi.error(
				"No se pueden eliminar líneas de una factura sincronizada con Business Central"
			);
			return;
		}

		if (!lines || lines.length === 0) {
			window.messageApi.info("No hay líneas para eliminar");
			return;
		}

		if (invoice && invoice.Document_Type === "Credit Memo") {
			window.messageApi.error(
				"No se pueden eliminar líneas en notas de crédito"
			);
			return;
		}

		const applyDeleteAll = async () => {
			try {
				const visibleLines = lines.filter((line) => !line._isDeleted);
				const persistedLines = visibleLines.filter(
					(line) => isInvoiceEditing && line.id && !line._isLocalLine
				);

				setLines([]);

				let failedDeletes = 0;
				if (persistedLines.length > 0) {
					const results = await Promise.allSettled(
						persistedLines.map((line) => deleteInvoiceLine(line.id))
					);
					failedDeletes = results.filter((r) => r.status === "rejected").length;
				}

				if (failedDeletes > 0) {
					window.messageApi.warning(
						`Se eliminaron las líneas en pantalla, pero ${failedDeletes} no pudieron eliminarse en base de datos.`
					);
					return;
				}

				window.messageApi.success("Todas las líneas fueron eliminadas");
			} catch (error) {
				console.error("Error al eliminar todas las líneas:", error);
				window.messageApi.error("No se pudieron eliminar todas las líneas");
			}
		};

		setPendingDiscountData({
			type: "deleteAllLines",
			applyDeleteAll,
		});
		setAdminAuthModal(true);
	};

	// Editar cantidad de una línea
	const handleQuantityChange = async (line, newQuantity) => {
		// Para facturas normales (no notas de crédito), la cantidad no puede ser menor a 1
		if (invoice && invoice.Document_Type !== "Credit Memo" && newQuantity < 1) {
			window.messageApi.warning(
				"En las facturas, la cantidad mínima es 1. Para cantidades negativas use notas de crédito."
			);
			setTempQuantity(1);
			return;
		}

		// Para abonos, verificar que la cantidad no exceda el límite disponible
		if (invoice && invoice.Document_Type === "Credit Memo") {
			// La cantidad debe ser negativa para abonos
			if (newQuantity > 0) {
				newQuantity = -Math.abs(newQuantity);
			}

			// Verificar si hay límite de devolución (en abonos parciales)
			if (line.remainingQuantity !== undefined) {
				// Convertir a valores absolutos para comparación
				const absNewQuantity = Math.abs(newQuantity);
				const absRemainingQuantity = Math.abs(line.remainingQuantity);

				// Si la cantidad excede lo disponible, limitar
				if (absNewQuantity > absRemainingQuantity) {
					window.messageApi.warning(
						`Solo puede devolver hasta ${absRemainingQuantity} unidades de este producto`
					);
					newQuantity = -absRemainingQuantity;
				}
			}
		}

		// Sólo actualizamos el valor temporal
		setTempQuantity(newQuantity);
	};

	// Nueva función para confirmar el cambio de cantidad
	const handleQuantityConfirm = async (line) => {
		if (isSynchronized) {
			window.messageApi.error(
				"No se pueden editar líneas de una factura sincronizada con Business Central"
			);
			return;
		}

		// Si no hay un valor temporal o es el mismo que tenemos, no hacemos nada
		if (tempQuantity === null || tempQuantity === line.Quantity) {
			setEditingLine(null);
			setTempQuantity(null);
			return;
		}

		try {
			const updatedLines = lines.map((l) => {
				if (l.key === line.key) {
					return { ...l, Quantity: tempQuantity };
				}
				return l;
			});

			setLines(updatedLines);

			if (isInvoiceEditing && line.id) {
				await updateInvoiceLine(line.id, { ...line, Quantity: tempQuantity });
			}

			// Si es una nota de crédito, recalcular los importes de pago automáticamente
			if (
				invoice &&
				invoice.Document_Type === "Credit Memo" &&
				onPaymentLinesUpdate
			) {
				await recalculatePaymentLinesForCreditMemo(updatedLines);
			}

			setEditingLine(null);
			setTempQuantity(null);
		} catch (error) {
			console.error("Error al actualizar cantidad:", error);
			window.messageApi.error("No se pudo actualizar la cantidad");
		}
	};

	// Función para recalcular las líneas de pago en notas de crédito cuando cambian las cantidades
	const recalculatePaymentLinesForCreditMemo = async (updatedLines) => {
		if (!invoice?.paymentLines) return;

		// 1. Calcular el total real de productos devueltos (CON IVA)
		const returnedTotalWithVat = updatedLines.reduce((sum, line) => {
			const { total } = calculateLineAmounts({
				unitPrice: line.Unit_Price,
				qty: Math.abs(line.Quantity || 0),
				discountPct: line.Line_Discount_ || 0,
				ivaPct: line.PorIva || 0,
				priceIncludesVAT: line.Price_Includes_VAT === true,
				country,
			});

			return sum + total;
		}, 0);

		// 2. Recalcular líneas de pago manteniendo proporciones originales
		const updatedPaymentLines = invoice.paymentLines.map((paymentLine) => {
			// Si la línea tiene proporción guardada, usarla
			if (paymentLine.paymentProportion !== undefined) {
				let newAmount = returnedTotalWithVat * paymentLine.paymentProportion;

				// Aplicar descuento de método de pago solo si había UNA sola forma de pago
				const paymentDiscount = paymentLine.Dto || 0;
				const hadMultiplePaymentMethods =
					invoice.hadMultiplePaymentMethods || false;

				if (paymentDiscount > 0 && !hadMultiplePaymentMethods) {
					newAmount = newAmount * (1 - paymentDiscount / 100);
				}

				return {
					...paymentLine,
					Amount: newAmount,
				};
			}

			// Si no tiene proporción guardada, mantener el importe actual
			// (para líneas creadas manualmente o casos especiales)
			return paymentLine;
		});

		// 3. Notificar al componente padre
		if (onPaymentLinesUpdate) {
			onPaymentLinesUpdate(updatedPaymentLines);
		}
	};

	const handleApplyGlobalDiscount = async () => {
		const { maxLineDiscount, requireAuthorizationForDiscount } = invoiceLinesConfig;

		if (globalDiscountValue < 0 || globalDiscountValue > maxLineDiscount) {
			window.messageApi.warning(`El descuento debe estar entre 0% y ${maxLineDiscount}%`);
			return;
		}

		const isAuthRequired = requireAuthorizationForDiscount(globalDiscountValue);

		const applyToAll = async () => {
			const updatedLines = lines.map(line => ({
				...line,
				Line_Discount_: globalDiscountValue
			}));

			setLines(sanitizeLines(updatedLines));

			// If editing existing invoice, update all lines in DB
			if (isInvoiceEditing) {
				await Promise.all(updatedLines.map(line =>
					line.id ? updateInvoiceLine(line.id, line) : Promise.resolve()
				));
			}

			window.messageApi.success(`Descuento del ${globalDiscountValue}% aplicado a todas las líneas`);
			setGlobalDiscountModal(false);
		};

		if (isAuthRequired) {
			setPendingDiscountData({ isGlobal: true, newDiscount: globalDiscountValue, applyToAll });
			setAdminAuthModal(true);
		} else {
			await applyToAll();
		}
	};

	const currentSelectedLinePrice = Number(
		selectedPriceIncreaseLine?.Unit_Price || 0
	);
	const parsedNewPriceValue = Number(newPriceValue);
	const isValidPriceIncrease =
		Number.isFinite(parsedNewPriceValue) &&
		parsedNewPriceValue > currentSelectedLinePrice;

	// Columnas para la tabla de productos
	const columns = [
		{
			title: "Producto",
			dataIndex: "Description",
			key: "Description",
			width: isMobile ? "40%" : "35%",
			ellipsis: isMobile,
			render: (text) =>
				isMobile ? (
					<Typography.Text ellipsis={{ tooltip: text }}>{text}</Typography.Text>
				) : (
					text
				),
		},
		{
			title: "Precio",
			dataIndex: "Unit_Price",
			key: "Unit_Price",
			width: "15%",
			render: (price, record) => (
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
					<span>{formatNumberByCountry((price || 0), country, true)}</span>
					{canApplyPriceIncrease &&
						<Tooltip title="Aumentar precio (requiere supervisor)">
							<Button
								size="small"
								type="text"
								icon={<PlusOutlined style={{ fontSize: "12px" }} />}
								onClick={(e) => {
									e.stopPropagation();
									handleOpenPriceIncreaseModal(record);
								}}
							/>
						</Tooltip>
					}
				</div>
			),
		},
		{
			title: "Cant.",
			dataIndex: "Quantity",
			key: "Quantity",
			width: "15%",
			render: (_, record) => {
				if (editingLine === record.key) {
					// Para facturas normales, min=1. Para notas de crédito, no hay límite mínimo
					const minValue =
						invoice && invoice.Document_Type === "Credit Memo" ? undefined : 1;

					return (
						<InputNumber
							disabled={invoice && invoice.Document_Type === "Credit Memo"}
							precision={2}
							min={minValue}
							value={tempQuantity !== null ? tempQuantity : record.Quantity}
							onChange={(value) => handleQuantityChange(record, value)}
							onBlur={() => handleQuantityConfirm(record)}
							onPressEnter={() => handleQuantityConfirm(record)}
							autoFocus
							style={{ width: "100%" }}
						/>
					);
				}

				// Si es un abono y hay información de cantidades previamente abonadas
				if (
					invoice &&
					invoice.Document_Type === "Credit Memo" &&
					record.originalQuantity !== undefined &&
					record.alreadyAbonedQuantity !== undefined
				) {
					return (
						<Tooltip
							title={
								`Original: ${record.originalQuantity}, ` +
								`Ya abonado: ${record.alreadyAbonedQuantity}, ` +
								`Disponible: ${record.remainingQuantity}`
							}
						>
							<div
								onClick={() => {
									if (!isSynchronized) {
										setEditingLine(record.key);
										setTempQuantity(record.Quantity);
									}
								}}
								style={{
									cursor: isSynchronized ? "default" : "pointer",
									display: "flex",
									flexDirection: "column",
								}}
							>
								<span>{record.Quantity}</span>
								<Tag color="blue" style={{ marginTop: 4 }}>
									Disp: {record.remainingQuantity}
								</Tag>
							</div>
						</Tooltip>
					);
				}

				return (
					<div
						onClick={() => {
							if (!isSynchronized) {
								setEditingLine(record.key);
								setTempQuantity(record.Quantity);
							}
						}}
						style={{ cursor: isSynchronized ? "default" : "pointer" }}
					>
						{record.Quantity}
					</div>
				);
			},
		},
		{
			title: (
				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
					<span>Dto.</span>
					<Tooltip title="Aplicar descuento a todo">
						<Button
							size="small"
							type="text"
							icon={<PlusOutlined style={{ fontSize: '12px' }} />}
							onClick={(e) => {
								e.stopPropagation();
								setGlobalDiscountModal(true);
							}}
						/>
					</Tooltip>
				</div>
			),
			dataIndex: "Line_Discount_",
			key: "Line_Discount_",
			width: "15%",
			render: (discount, record) => {
				if (editingDiscount === record.key) {
					return (
						<InputNumber
							min={0}
							max={100}
							value={tempDiscount !== null ? tempDiscount : record.Line_Discount_}
							onChange={(value) => handleDiscountChange(record, value)}
							onBlur={() => handleDiscountConfirm(record)}
							onPressEnter={() => handleDiscountConfirm(record)}
							autoFocus
							style={{ width: "100%" }}
							precision={2}
							formatter={(value) => {
								if (value === undefined || value === null || value === '') return '';
								return `${parseFloat(value).toFixed(2)}%`;
							}}
							parser={(value) => value.replace('%', '')}
						/>
					);
				}

				const clientDiscount = record.Client_Discount_ || 0;
				const couponDiscount = record.Coupon_Discount_ || 0;
				const totalDiscount = record.Line_Discount_ || 0;

				return (
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: "2px",
							cursor: isSynchronized ? "default" : "pointer",
						}}
						onClick={() => {
							if (!isSynchronized) {
								setEditingDiscount(record.key);
								setTempDiscount(record.Line_Discount_);
							}
						}}
						title={
							isSynchronized
								? "No se pueden editar descuentos en facturas sincronizadas"
								: "Haga clic para editar descuento (requiere autorización de administrador)"
						}
					>
						<span style={{ fontWeight: "bold" }}>
							{totalDiscount ? `${totalDiscount}%` : "-"}
							{!isSynchronized && (
								<LockOutlined
									style={{
										marginLeft: 4,
										fontSize: "10px",
										color: "#999",
										opacity: 0.7,
									}}
								/>
							)}
						</span>
						{clientDiscount > 0 && (
							<Tag color="blue" size="small">
								Cliente: {clientDiscount}%
							</Tag>
						)}
						{couponDiscount > 0 && (
							<Tag color="green" size="small">
								Cupón: {couponDiscount}%
							</Tag>
						)}
					</div>
				);
			},
			responsive: ["md"],
		},
		{
			title: "IVA",
			dataIndex: "PorIva",
			key: "PorIva",
			width: "10%",
			render: (iva) => `${(iva || 0).toFixed(2)}%`,
			responsive: ["md"],
		},
		{
			title: "Importe",
			key: "Amount",
			width: isMobile ? "20%" : "15%",
			render: (_, record) => {
				const { total } = calculateLineAmounts({
					unitPrice: record.Unit_Price,
					qty: record.Quantity,
					discountPct: record.Line_Discount_,
					ivaPct: record.PorIva,
					priceIncludesVAT: record.Price_Includes_VAT,
					country,
				});

				return formatNumberByCountry(total, country, true);
			},
		},
		{
			title: "",
			key: "actions",
			width: isMobile ? "10%" : "10%",
			render: (_, record) => {
				const isDisabled = isSynchronized;
				const tooltipText = isDisabled
					? "No se pueden eliminar líneas de facturas sincronizadas con Business Central"
					: "Eliminar línea";

				return (
					<Tooltip title={tooltipText}>
						<Button
							icon={<DeleteOutlined />}
							size="small"
							danger
							onClick={() => handleDeleteLine(record)}
							disabled={
								isDisabled ||
								(invoice && invoice.Document_Type === "Credit Memo")
							}
							style={{
								opacity: isDisabled ? 0.5 : 1,
								cursor: isDisabled ? "not-allowed" : "pointer",
							}}
						/>
					</Tooltip>
				);
			},
		},
	];

	return (
		<div className="invoice-lines-container">
			<Card className="scan-area" style={{ marginBottom: 16 }}>
				<Row gutter={[16, 16]} align="middle">
					<Col xs={24} sm={24} md={10}>
						<Input
							placeholder={
								isCreditMemo
									? "No se pueden añadir líneas a notas de crédito"
									: "Escanear código de barras o producto"
							}
							value={scanCode}
							onChange={(e) => setScanCode(e.target.value)}
							onPressEnter={(e) => {
								e.preventDefault(); // Prevenir el envío del formulario
								if (scanCode.trim() && !loadingProduct && !isCreditMemo) {
									handleScan();
								}
							}}
							disabled={isSynchronized || loadingProduct || isCreditMemo}
							ref={scanInputRef}
							size="large"
							autoFocus={isInvoiceEditing && !isCreditMemo}
						/>
					</Col>
					<Col xs={16} sm={16} md={10}>
						<Select
							showSearch
							placeholder={
								isCreditMemo
									? "No disponible para notas de crédito"
									: "O seleccionar de la lista"
							}
							value={selectedProduct}
							onChange={handleProductSelect}
							style={{ width: "100%" }}
							size="large"
							disabled={isSynchronized || loadingProduct || isCreditMemo}
							loading={loadingProduct}
							optionFilterProp="children"
							filterOption={(input, option) =>
								(option?.label || "")
									.toLowerCase()
									.includes(input.toLowerCase())
							}
							allowClear
						>
							{products.map((product) => (
								<Select.Option
									key={product.No}
									value={product.No}
									label={`${product.No} - ${product.description}`}
									title={`${product.No} - ${product.description}`}
								>
									{product.No} - {product.description}
								</Select.Option>
							))}
						</Select>
					</Col>
					<Col xs={8} sm={8} md={4}>
						<Space
							style={{ width: "100%", justifyContent: isMobile ? "stretch" : "space-evenly" }}
						>
							<Button
								type="primary"
								size="large"
								icon={<PlusOutlined />}
								onClick={handleScan}
								disabled={isSynchronized || !scanCode.trim() || loadingProduct}
								loading={loadingProduct}
							>
								{!isMobile && (loadingProduct ? "Agregando..." : "Agregar")}
							</Button>

							<Tooltip
								title={
									isSynchronized
										? "No se pueden eliminar líneas en facturas sincronizadas"
										: invoice?.Document_Type === "Credit Memo"
											? "No se pueden eliminar líneas en notas de crédito"
											: "Eliminar todas las líneas de la factura"
								}
							>
								<Button
									type="default"
									size="large"
									danger
									icon={<DeleteOutlined />}
									onClick={handleDeleteAllLines}
									disabled={isSynchronized || invoice?.Document_Type === "Credit Memo"}
								>
									{!isMobile && "Borrar"}
								</Button>
							</Tooltip>
						</Space>
					</Col>
				</Row>
			</Card>

			<Table
				columns={columns}
				dataSource={lines.filter((line) => !line._isDeleted)}
				rowKey="key"
				loading={loading}
				className="invoice-lines-table"
				scroll={{ x: isMobile ? 500 : true, y: 300 }}
				pagination={false}
				size={isMobile ? "small" : "middle"}
			/>

			<div
				className="invoice-totals"
				style={{
					display: "flex",
					flexDirection: isMobile ? "column" : "column",
					alignItems: isMobile ? "flex-end" : "flex-end",
					marginTop: "16px",
				}}
			>
				<div
					className="invoice-totals-item"
					style={{
						display: "flex",
						justifyContent: "space-between",
						width: isMobile ? "100%" : "250px",
						padding: "4px 0",
					}}
				>
					<span>Subtotal:</span>
					<span>{formatNumberByCountry(totals.subtotal, country, true)}</span>
				</div>
				<div
					className="invoice-totals-item"
					style={{
						display: "flex",
						justifyContent: "space-between",
						width: isMobile ? "100%" : "250px",
						padding: "4px 0",
					}}
				>
					<span>IVA:</span>
					<span>{formatNumberByCountry(totals.vat, country, true)}</span>
				</div>
				<div
					className="invoice-totals-item grand-total"
					style={{
						display: "flex",
						justifyContent: "space-between",
						width: isMobile ? "100%" : "250px",
						padding: "8px 0",
						fontWeight: "bold",
						fontSize: isMobile ? "16px" : "18px",
					}}
				>
					<span>Total:</span>
					<span>{formatNumberByCountry(totals.baseTotal, country, true)}</span>
				</div>
			</div>

			{/* Modal de advertencia para productos sin precio */}
			<Modal
				title="No hay precio para este producto y cliente"
				open={zeroPriceModalVisible}
				onCancel={() => {
					setZeroPriceModalVisible(false);
					setZeroPriceProductInfo(null);
					setTimeout(() => {
						scanInputRef.current?.focus();
					}, 100);
				}}
				footer={[
					<Button
						key="ok"
						type="primary"
						onClick={() => {
							setZeroPriceModalVisible(false);
							setZeroPriceProductInfo(null);
							setTimeout(() => {
								scanInputRef.current?.focus();
							}, 100);
						}}
					>
						Aceptar
					</Button>,
				]}
				centered
				maskClosable={false}
				closable={false}
			>
				<p>No hay precio configurado para este producto y cliente:</p>
				<p>
					<strong>Producto:</strong> {zeroPriceProductInfo?.name} (No:{" "}
					<strong>{zeroPriceProductInfo?.no}</strong>)
				</p>
				<p>
					<strong>Cliente:</strong> {zeroPriceProductInfo?.clientName} (No:{" "}
					<strong>{zeroPriceProductInfo?.clientNo}</strong>)
				</p>
				<p>
					Configure el precio del producto antes
					de volver a agregarlo.
				</p>
			</Modal>

			{/* Modal de subida de precio */}
			<Modal
				title="Aumentar precio"
				open={priceIncreaseModalVisible}
				onOk={handleConfirmPriceIncrease}
				onCancel={() => {
					setPriceIncreaseModalVisible(false);
					setNewPriceValue(null);
					setSelectedPriceIncreaseLine(null);
					setPendingDiscountData(null);
				}}
				okText="Solicitar autorización"
				cancelText="Cancelar"
				destroyOnHidden
				okButtonProps={{ disabled: !isValidPriceIncrease }}
			>
				<div style={{ marginBottom: 12 }}>
					<Typography.Text>
						Precio actual: {formatNumberByCountry(currentSelectedLinePrice, country, true)}
					</Typography.Text>
					<br />
					<Typography.Text type="secondary">
						Ingrese un precio mayor al actual.
					</Typography.Text>
				</div>
				<InputNumber
					min={Number((currentSelectedLinePrice + 0.01).toFixed(2))}
					precision={2}
					value={newPriceValue}
					onChange={(value) =>
						setNewPriceValue(value === null ? null : Number(value))
					}
					parser={(value) => {
						if (!value) return "";
						const sanitizedValue = String(value)
							.replaceAll(" ", "")
							.replaceAll(".", "")
							.replaceAll(",", ".")
							.replaceAll(/[^\d.-]/g, "");
						return sanitizedValue;
					}}
					style={{ width: "100%" }}
					placeholder="Nuevo precio"
				/>
			</Modal>
			{/* Modal de autorización de administrador para descuentos */}
			<Modal
				title={
					<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
						<LockOutlined />
						<span>Autorización de Administrador</span>
					</div>
				}
				open={adminAuthModal}
				onCancel={handleCancelAdminAuth}
				footer={null}
				centered
				maskClosable={false}
			>
				<div style={{ marginBottom: "16px" }}>
					<Alert
						message="Autorización requerida"
						description="Se requiere autorización de supervisor para aplicar descuentos, subidas de precio o borrar todas las líneas de factura."
						type="warning"
						showIcon
					/>
				</div>

				<Form
					form={adminForm}
					layout="vertical"
					onFinish={handleAdminAuthForDiscount}
				>
					<Form.Item
						name="username"
						label="Usuario"
						rules={[
							{
								required: true,
								message: "Ingrese el usuario de administrador",
							},
						]}
					>
						<Input
							prefix={<UserOutlined />}
							placeholder="Usuario de administrador"
							autoComplete="username"
						/>
					</Form.Item>

					<Form.Item
						name="password"
						label="Contraseña"
						rules={[
							{
								required: true,
								message: "Ingrese la contraseña de administrador",
							},
						]}
					>
						<Input.Password
							prefix={<LockOutlined />}
							placeholder="Contraseña de administrador"
							autoComplete="current-password"
						/>
					</Form.Item>

					<div style={{ textAlign: "right", marginTop: "24px" }}>
						<Button
							onClick={handleCancelAdminAuth}
							style={{ marginRight: "8px" }}
						>
							Cancelar
						</Button>
						<Button type="primary" htmlType="submit" loading={adminAuthLoading}>
							Autorizar
						</Button>
					</div>
				</Form>
			</Modal>
			{/* Modal for Global Discount */}
			<Modal
				title="Descuento Global"
				open={globalDiscountModal}
				onOk={handleApplyGlobalDiscount}
				onCancel={() => setGlobalDiscountModal(false)}
				okText="Aplicar"
				cancelText="Cancelar"
				destroyOnHidden
			>
				<div style={{ marginBottom: 16 }}>
					<Typography.Text>Ingrese el descuento a aplicar a todas las líneas:</Typography.Text>
				</div>
				<InputNumber
					min={0}
					max={100}
					formatter={value => `${value}%`}
					parser={value => value.replace('%', '')}
					style={{ width: '100%' }}
					value={globalDiscountValue}
					onChange={setGlobalDiscountValue}
					autoFocus
				/>
			</Modal>
		</div>
	);
};

export default InvoiceLines;
